import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  Card,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { getProjectCohortPath } from '@verantos-dev/core-constants';
import { ArtifactStatusChip, Icon } from '@verantos-dev/shared-components';
import { borderRadius, opacity } from '@verantos-dev/shared-ui-components/tokens';

import { AddCohortPickerModal } from './AddCohortPickerModal';
import { SectionHeader } from '../../components/SectionHeader';
import { SubtleSurface } from '../../components/SubtleSurface';
import { useAdRouteParams } from '../../hooks/useAdRouteParams';
import { useSourceCohortsSection } from '../../hooks/useSourceCohortsSection';
import type { SourceCohortsSectionProps } from '../../types';
import { toAssetStatus } from '../../utils/assetStatusLabel';
import { formatIsoDate } from '../../utils/formatIsoDate';

export const SourceCohortsSection = ({ isLoading = false }: SourceCohortsSectionProps) => {
  const { projectId } = useAdRouteParams();
  const navigate = useNavigate();
  const {
    sectionLabel,
    count,
    addLabel,
    emptyTitle,
    emptyBody,
    removeLabel,
    staleCohortTooltip,
    rows,
    isPickerOpen,
    isLoading: isSectionLoading,
    onAddCohort,
    onClosePicker,
    onRemoveCohort,
    isEditDisabled,
    editLockTooltip,
  } = useSourceCohortsSection();

  // Parent covers "store hasn't hydrated yet"; section covers "store has a
  // cohort attached but the cohort list lookup hasn't resolved yet".
  const showSkeleton = isLoading || isSectionLoading;

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          // Figma: section card padding = spacing-6 (24px).
          p: 6,
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          // Figma: 24px title-to-content gap.
          gap: 6,
          minWidth: 0,
        }}
      >
        {!showSkeleton && rows.length === 0 ? (
          // Empty state (Figma): no section header, no inner surface — centered
          // heading + supporting copy + a centered Add button, directly on the
          // white section card. `flex: 1` fills the card's fixed min-height.
          <Box
            sx={{
              p: 4,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              textAlign: 'center',
            }}
            data-testid="ad-source-cohorts-empty"
          >
            <Stack spacing={2} sx={{ alignItems: 'center' }}>
              {/* Figma empty-state heading: h5 (24px) regular weight. */}
              <Typography variant="h5">{emptyTitle}</Typography>
              {/* Figma body: body1 (16px) secondary. */}
              <Typography variant="body1" color="text.secondary">
                {emptyBody}
              </Typography>
            </Stack>
            <Tooltip title={editLockTooltip ?? ''} disableHoverListener={!isEditDisabled}>
              {/* span wrapper so the tooltip still shows on the disabled button */}
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={onAddCohort}
                  disabled={isEditDisabled}
                  sx={{ textTransform: 'none' }}
                  data-testid="ad-source-cohorts-add"
                >
                  {addLabel}
                </Button>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <>
            <SectionHeader
              label={sectionLabel}
              count={count}
              action={{
                label: addLabel,
                onClick: onAddCohort,
                disabled: isEditDisabled,
                tooltip: editLockTooltip,
                dataTestId: 'ad-source-cohorts-add',
              }}
            />
            {showSkeleton ? (
              <Skeleton
                variant="rounded"
                height={84}
                animation="wave"
                sx={{ borderRadius: `${borderRadius.md}px` }}
                data-testid="ad-source-cohorts-loading"
              />
            ) : (
              <Stack spacing={1.5}>
                {rows.map((row) => {
                  const dateRange =
                    row.studyPeriodStart && row.studyPeriodEnd
                      ? `${formatIsoDate(row.studyPeriodStart)} – ${formatIsoDate(
                          row.studyPeriodEnd
                        )}`
                      : null;
                  // Not-yet-PROCESSED cohorts have no real subject count
                  // (`row.subjectCount` is null) — show a "--" placeholder
                  // rather than hiding the segment, matching CohortDetailHeader.
                  const subjects =
                    row.subjectCount !== null && row.subjectCount >= 0
                      ? `${row.subjectCount.toLocaleString()} subjects`
                      : 'No subjects';
                  const metaLine = [dateRange, subjects].filter(Boolean).join(' • ');
                  return (
                    <SubtleSurface
                      key={row.cohortProfileId}
                      // Figma READ ONLY VIEW box: 24px/16px padding, 48px
                      // content row (matches the cohort study-period box).
                      sx={{
                        paddingX: 6,
                        paddingY: 4,
                        minHeight: 80,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                      data-testid={`ad-source-cohort-${row.cohortProfileId}`}
                    >
                      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                          gap={2}
                        >
                          {/* Figma: cohort name is a primary-blue link that opens
                              the cohort. body1_medium (16px / weight 500). */}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              navigate(getProjectCohortPath(projectId, row.cohortProfileId))
                            }
                            sx={{
                              alignSelf: 'flex-start',
                              minWidth: 0,
                              maxWidth: '100%',
                              // `typography` expands to the full body1 variant, which
                              // carries color: neutral-900 — set the link color AFTER it
                              // or the primary blue gets overwritten with black.
                              typography: 'body1',
                              fontWeight: 500,
                              color: 'primary.main',
                              textAlign: 'left',
                            }}
                            data-testid={`ad-source-cohort-link-${row.cohortProfileId}`}
                          >
                            {row.cohortName}
                          </Link>
                          {/* Same status vocabulary/chip as the AD detail header. */}
                          <ArtifactStatusChip
                            status={toAssetStatus(row.status)}
                            data-testid={`ad-source-cohort-status-${row.cohortProfileId}`}
                          />
                          {row.isStale && (
                            <Tooltip title={staleCohortTooltip} arrow>
                              <WarningIcon
                                fontSize="small"
                                color="warning"
                                data-testid={`ad-source-cohort-stale-${row.cohortProfileId}`}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                        {metaLine && (
                          <Typography variant="body2" color="text.secondary">
                            {metaLine}
                          </Typography>
                        )}
                      </Stack>
                      {/* Per-row remove (matches the Criteria card delete): drops
                          the cohort, which also clears the inherited study period.
                          span wrapper so the tooltip still shows while disabled. */}
                      <Tooltip title={editLockTooltip ?? ''} disableHoverListener={!isEditDisabled}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveCohort(row.cohortProfileId)}
                            disabled={isEditDisabled}
                            aria-label={removeLabel}
                            data-testid={`ad-source-cohort-remove-${row.cohortProfileId}`}
                            // Same disabled treatment as the criteria card delete:
                            // faded red glyph, not the gray deleteDisabled icon.
                            sx={{ '&.Mui-disabled': { opacity: opacity[38] } }}
                          >
                            <Icon
                              id={`ad-source-cohort-delete-${row.cohortProfileId}`}
                              icon="delete"
                              height={24}
                              width={24}
                            />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </SubtleSurface>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </Card>
      {isPickerOpen && <AddCohortPickerModal projectId={projectId} onClose={onClosePicker} />}
    </>
  );
};
